/**
 * PATH: packages/bills/src/components/virtualized-list.ts
 * FILE_ID: BILLS-PKG-CMP-VLIST-001
 * PURPOSE: Threshold-gated, variable-height list windowing for the bills
 *          left-rail. Wraps `@tanstack/react-virtual` and hands the panel
 *          back everything it needs to render EITHER a plain list (small
 *          lists) or an absolutely-positioned window (large lists).
 *
 * WHY: The rail now uses infinite scroll and can grow to thousands of
 *      grouped rows. Rendering every row's React tree into the DOM makes
 *      scroll sluggish and first paint slow. This keeps only the visible
 *      window (+ overscan) mounted.
 *
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────────┐   ┌────────────────────┐
 *   │ item count + │ → │ useListVirtualizer() │ → │ enabled + window +  │
 *   │ scrollRef    │   │ (this module)        │   │ measure props       │
 *   └──────────────┘   └──────────────────────┘   └────────────────────┘
 *
 * SAFETY: Windowing only engages once `count > threshold`. Below it the
 *      caller renders every row exactly as its plain `.map()` did — same
 *      DOM, keys, borders — so the common first-page case is unchanged and
 *      carries zero regression risk. Mirrors the proven pattern already in
 *      `@quotech/email-hub` (_internal/VirtualizedRows).
 *
 * The scroll element is OWNED BY THE CALLER (passed via `scrollRef`) so the
 * SAME element that drives the panel's infinite-scroll `onScroll` sentinel
 * is the virtualizer's scroll container — the two never fight over scroll.
 *
 * DEPENDENCIES:
 *   - @tanstack/react-virtual: the windowing engine (variable-height via
 *     measureElement, so we never hard-code a row height).
 *
 * RELATED:
 *   - BillsListPanel.tsx (sole consumer)
 *   - packages/email-hub/src/_internal/components/VirtualizedRows.tsx (the
 *     sibling pattern this mirrors)
 */

import { useEffect, type CSSProperties, type RefObject } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

/**
 * Props a windowed row must spread onto its own outer element so the
 * virtualizer can (a) measure its real height and (b) position it. The
 * caller keeps ownership of the element type (e.g. `<li>`) so list
 * semantics and styling are untouched.
 */
export interface WindowRowProps {
  /** Attach to the row's outer element for dynamic height measurement. */
  ref: (el: HTMLElement | null) => void;
  /** Read by the virtualizer to map a measured element back to its index. */
  'data-index': number;
  /** Absolute positioning + translateY offset for the row. */
  style: CSSProperties;
}

export interface UseListVirtualizerArgs {
  /** Number of render-items (headers + rows) in the stream. */
  count: number;
  /** The panel's existing `overflow-y-auto` scroll element. */
  scrollRef: RefObject<HTMLDivElement | null>;
  /**
   * Best-guess px height for the item at `index` (headers are shorter than
   * rows). Only seeds the initial layout / scrollbar — real heights are
   * measured per-row after mount, so estimates need not be exact.
   */
  estimateSize: (index: number) => number;
  /** Rows kept mounted above/below the viewport. */
  overscan?: number;
  /**
   * Window only once `count` exceeds this. At or below it every item
   * renders plainly (identical to the pre-virtualisation output). Chosen a
   * little above the first infinite-scroll page (50) so the common
   * single-page case never windows and the flip happens at most once as the
   * user pages in more rows.
   */
  threshold?: number;
  /**
   * Keep this item index scrolled into view on the windowed path (e.g. the
   * selected bill). Ignored when null / out of range.
   */
  activeIndex?: number | null;
}

export interface ListVirtualizer {
  /** True when the windowed path is active (count over threshold). */
  enabled: boolean;
  /** Total scrollable height for the sized container (windowed path). */
  totalSize: number;
  /** The visible window: `{ index, start }` per item to render. React keys
   *  are owned by the caller's render fn (item.key / row.id), so we don't
   *  surface the virtualizer's own key here. */
  items: Array<{ index: number; start: number }>;
  /** Build the props to spread onto a windowed row's outer element. */
  rowProps: (index: number, start: number) => WindowRowProps;
}

/**
 * pure-ish hook: (count, scrollRef, sizing) → windowing state.
 *
 * The `useVirtualizer` hook is ALWAYS called (rules-of-hooks); when
 * disabled we feed it `count: 0` so it does no work and the caller renders
 * its plain list instead.
 */
export function useListVirtualizer({
  count,
  scrollRef,
  estimateSize,
  overscan = 10,
  threshold = 60,
  activeIndex = null,
}: UseListVirtualizerArgs): ListVirtualizer {
  const enabled = count > threshold;

  const virtualizer = useVirtualizer({
    count: enabled ? count : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    overscan,
  });

  useEffect(() => {
    if (!enabled || activeIndex == null || activeIndex < 0 || activeIndex >= count) {
      return;
    }
    virtualizer.scrollToIndex(activeIndex, { align: 'auto' });
    // virtualizer identity is stable across renders; re-run only when the
    // target row (or enablement) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, activeIndex, count]);

  if (!enabled) {
    return { enabled: false, totalSize: 0, items: [], rowProps: neverRowProps };
  }

  return {
    enabled: true,
    totalSize: virtualizer.getTotalSize(),
    items: virtualizer.getVirtualItems().map((vi) => ({
      index: vi.index,
      start: vi.start,
    })),
    rowProps: (index, start) => ({
      ref: virtualizer.measureElement,
      'data-index': index,
      style: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        transform: `translateY(${start}px)`,
      },
    }),
  };
}

/** Placeholder for the disabled path — never invoked (items is empty). */
function neverRowProps(): WindowRowProps {
  throw new Error('rowProps called while list virtualization is disabled');
}
